import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHead } from "@/components/storefront/common";
import { Search, Loader2, PackageCheck } from "lucide-react";

const LABELS = {
  ORDER_PLACED: "Order Placed", PAYMENT_PENDING: "Payment Pending", PAYMENT_CONFIRMED: "Payment Confirmed",
  ORDER_CONFIRMED: "Order Confirmed", PROCESSING: "Processing", READY_TO_SHIP: "Ready to Ship",
  SHIPMENT_CREATED: "Shipment Created", SHIPPED: "Shipped", IN_TRANSIT: "In Transit",
  OUT_FOR_DELIVERY: "Out for Delivery", DELIVERED: "Delivered", CANCELLED: "Cancelled",
  REFUND_INITIATED: "Refund Initiated", REFUNDED: "Refunded",
};

export default function TrackOrder() {
  const [num, setNum] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const track = async (e) => {
    e.preventDefault();
    if (!num.trim()) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.get(`/track/${num.trim()}`);
      setResult(data);
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="container-x py-8 max-w-2xl">
      <PageHead title="Track Your Order" crumbs={[{ label: "Home", to: "/" }, { label: "Track Order" }]} />
      <form onSubmit={track} className="flex gap-2 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input value={num} onChange={(e) => setNum(e.target.value.toUpperCase())} placeholder="e.g. STORE-2026-000001" data-testid="track-input"
            className="w-full h-12 pl-10 pr-4 rounded-lg border border-slate-200 outline-none focus:border-amber-400 text-sm font-mono" />
        </div>
        <button className="px-6 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold" data-testid="track-btn">Track</button>
      </form>

      {loading && <div className="text-center py-8"><Loader2 className="h-8 w-8 animate-spin text-amber-500 mx-auto" /></div>}
      {error && <p className="text-center text-red-600 text-sm" data-testid="track-error">{error}</p>}

      {result && (
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="track-result">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs text-slate-500">Order</p>
              <p className="font-mono font-bold text-lg">{result.orderNumber}</p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-50 text-amber-700">
              <PackageCheck className="h-3.5 w-3.5" /> {LABELS[result.status] || result.status}
            </span>
          </div>
          <div className="relative pl-6">
            {result.timeline?.map((t, i) => (
              <div key={i} className="relative pb-6 last:pb-0">
                <span className="absolute -left-6 top-1 h-3 w-3 rounded-full bg-amber-500 ring-4 ring-amber-100" />
                {i < result.timeline.length - 1 && <span className="absolute -left-[19px] top-4 bottom-0 w-0.5 bg-slate-200" />}
                <p className="font-semibold text-slate-900 text-sm">{t.label || LABELS[t.status] || "Update"}</p>
                {t.note && <p className="text-xs text-slate-500">{t.note}</p>}
                <p className="text-xs text-slate-400 mt-0.5">{new Date(t.at).toLocaleString("en-IN")}</p>
              </div>
            ))}
          </div>
          {result.carrierTimeline?.length > 0 && (
            <div className="mt-6 border-t border-slate-100 pt-4" data-testid="carrier-timeline">
              <p className="text-xs font-semibold uppercase text-slate-400 mb-3">Courier updates</p>
              <div className="relative pl-6">
                {result.carrierTimeline.map((event, i) => (
                  <div key={`${event.at || "event"}-${i}`} className="relative pb-4 last:pb-0">
                    <span className="absolute -left-6 top-1 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-4 ring-emerald-50" />
                    {i < result.carrierTimeline.length - 1 && <span className="absolute -left-[18px] top-3.5 bottom-0 w-0.5 bg-slate-200" />}
                    <p className="font-medium text-slate-900 text-sm">{event.label || event.status}</p>
                    {event.location && <p className="text-xs text-slate-500">{event.location}</p>}
                    <p className="text-xs text-slate-400 mt-0.5">
                      {event.at ? new Date(event.at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
